import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import JournalNavigation from '../../components/JournalNavigation';
import LockIndicator from '../../components/LockIndicator';
import JournalStartModal from '../../components/JournalStartModal';
import { getJournalStartConfig } from '../../utils/journalConstants';
import { isFieldDisabled } from '../../utils/lockingUtils';
import confetti from 'canvas-confetti';
import './SelfAssessment.css';

const Transformation = () => {
    const [showStartModal, setShowStartModal] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const [formData, setFormData] = useState({
        // T1: My Progress - 14 days mood tracking (7x2 grid)
        day1Mood: '',
        day2Mood: '',
        day3Mood: '',
        day4Mood: '',
        day5Mood: '',
        day6Mood: '',
        day7Mood: '',
        day8Mood: '',
        day9Mood: '',
        day10Mood: '',
        day11Mood: '',
        day12Mood: '',
        day13Mood: '',
        day14Mood: '',
        progressNotes: '',

        // T2: Commit
        commitDate: '',
        commitGoals: '',

        // T3: Stress Management
        stressPriority: '',
        stressHabits: [],
        stressOther: '',

        // T4: Exercise
        exercisePriority: '',
        exerciseHabits: [],
        exerciseOther: '',

        // T5: Sleep
        sleepPriority: '',
        sleepHabits: [],
        sleepOther: '',

        // T6: Nutrition
        nutritionPriority: '',
        nutritionHabits: [],
        nutritionOther: ''
    });

    const [entryId, setEntryId] = useState(null);
    const [saveTimeout, setSaveTimeout] = useState(null);
    const [entryMetadata, setEntryMetadata] = useState({ lockedAt: null, startedAt: null, createdAt: null });
    const [emojiPopup, setEmojiPopup] = useState({ show: false, emoji: '', day: null, position: null });

    useEffect(() => {
        loadEntry();
    }, []);

    // Reload when cycle changes
    useEffect(() => {
        const handler = () => { loadEntry(); };
        window.addEventListener('cycle-changed', handler);
        return () => window.removeEventListener('cycle-changed', handler);
    }, []);

    useEffect(() => {
        if (saveTimeout) clearTimeout(saveTimeout);
        const timeout = setTimeout(() => {
            saveEntry();
        }, 2000);
        setSaveTimeout(timeout);

        return () => clearTimeout(timeout);
    }, [formData]);

    const loadEntry = async () => {
        try {
            const response = await api.get('/journal', {
                params: { section: 'transformation' }
            });
            if (response.data.entries && response.data.entries.length > 0) {
                const entry = response.data.entries[0];
                setEntryId(entry._id);
                setFormData({ ...formData, ...entry.data });
                setEntryMetadata({
                    lockedAt: entry.lockedAt,
                    startedAt: entry.startedAt,
                    createdAt: entry.createdAt
                });
                setHasStarted(!!entry.startedAt);
            }
        } catch (error) {
            console.error('Error loading transformation entry:', error);
        }
    };

    const saveEntry = async (includeStartTime = false) => {
        try {
            const payload = {
                section: 'transformation',
                data: formData
            };

            if (includeStartTime) {
                payload.startedAt = new Date().toISOString();
            }

            if (entryId) {
                const response = await api.patch(`/journal/${entryId}`, payload);
                if (response.data.entry && response.data.entry.startedAt) {
                    setEntryMetadata(prev => ({
                        ...prev,
                        startedAt: response.data.entry.startedAt
                    }));
                }
            } else {
                const response = await api.post('/journal', payload);
                setEntryId(response.data.entry._id);
                setEntryMetadata({
                    lockedAt: response.data.entry.lockedAt,
                    startedAt: response.data.entry.startedAt,
                    createdAt: response.data.entry.createdAt
                });
            }
        } catch (error) {
            console.error('Error saving transformation entry:', error);
        }
    };

    const handleChange = (field, value) => {
        if (!hasStarted) {
            if (!showStartModal) {
                setShowStartModal(true);
            }
            return; // Don't update form until user clicks "Begin"
        }
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleBegin = () => {
        setShowStartModal(false);
        setHasStarted(true);
        saveEntry(true);
    };

    const handleNotNow = () => {
        setShowStartModal(false);
        // Stay on the same page - user can try again later
    };

    const handleCheckboxChange = (field, value) => {
        if (!hasStarted) {
            if (!showStartModal) {
                setShowStartModal(true);
            }
            return; // Don't update form until user clicks "Begin"
        }
        const currentValues = formData[field] || [];
        const newValues = currentValues.includes(value)
            ? currentValues.filter(v => v !== value)
            : [...currentValues, value];
        setFormData(prev => ({ ...prev, [field]: newValues }));
    };

    const handleMoodClick = (day, mood, event) => {
        if (!hasStarted) {
            if (!showStartModal) {
                setShowStartModal(true);
            }
            return; // Don't update form until user clicks "Begin"
        }
        handleChange(`day${day}Mood`, mood);

        // Get the position of the clicked element for popup positioning
        const rect = event.target.getBoundingClientRect();
        const position = {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2
        };

        // Show popup
        setEmojiPopup({
            show: true,
            emoji: mood,
            day: day,
            position: position
        });

        // Trigger confetti
        confetti({
            particleCount: 100,
            spread: 70,
            colors: ['#8B4513', '#FFD700', '#D2B48C', '#FFFFFF'], // brown, gold, tan, white
            origin: {
                x: (rect.left + rect.width / 2) / window.innerWidth,
                y: (rect.top + rect.height / 2) / window.innerHeight
            }
        });

        // Hide popup after 1.5 seconds
        setTimeout(() => {
            setEmojiPopup({ show: false, emoji: '', day: null, position: null });
        }, 1500);
    };

    // Helper function to get ordinal suffix
    const getOrdinalSuffix = (num) => {
        const numStr = num.toString();
        if (numStr === '1') return '1st';
        if (numStr === '2') return '2nd';
        if (numStr === '3') return '3rd';
        if (numStr === '4') return '4th';
        return numStr + 'th';
    };

    const moodEmojis = ['😊', '😐', '☹️'];

    // Helper function to render habit text with inline inputs and dropdowns
    const renderHabitWithInputs = (habitText, habitKey, isChecked = false) => {
        // Handle special cases with dropdowns
        if (habitText.includes('Eat (two / three) meals a day')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Eat {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_meals`] || 'two'}
                            onChange={(e) => handleChange(`${habitKey}_meals`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px' }}
                        >
                            <option value="two">two</option>
                            <option value="three">three</option>
                        </select>
                    ) : '(two / three)'} meals a day
                </span>
            );
        }
        if (habitText.includes('Eat at least (one / two / three) sources of (fibre / vegetable) in each meal')) {
            return (
                <span style={{ fontSize: '15px', lineHeight: '1.5', color: '#2c2416' }}>
                    Eat at least {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_quantity`] || 'one'}
                            onChange={(e) => handleChange(`${habitKey}_quantity`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px', borderRadius: '3px' }}
                        >
                            <option value="one">one</option>
                            <option value="two">two</option>
                            <option value="three">three</option>
                        </select>
                    ) : '(one / two / three)'} sources of (fibre / vegetable) in each meal
                    {/* {isChecked ? (
                        <select
                            value={formData[`${habitKey}_type`] || 'fibre'}
                            onChange={(e) => handleChange(`${habitKey}_type`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '13px', borderRadius: '3px' }}
                        >
                            <option value="fibre">fibre</option>
                            <option value="vegetable">vegetable</option>
                        </select>
                    ) : '(fibre / vegetable)'} in each meal */}
                </span>
            );
        }
        if (habitText.includes('Eat a minimum of _____ grams of protein per day')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Eat a minimum of {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_protein`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_protein`, e.target.value)}
                            style={{
                                width: '60px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 2px',
                                outline: 'none'
                            }}
                            placeholder="000"
                        />
                    ) : '_____'} grams of protein per day
                </span>
            );
        }
        if (habitText.includes('Have (equal / less) carbs on my plate compared to protein')) {
            return (
                <span style={{ fontSize: '15px', lineHeight: '1.5', color: '#2c2416' }}>
                    Have {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_carbs`] || 'equal'}
                            onChange={(e) => handleChange(`${habitKey}_carbs`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px', borderRadius: '3px' }}
                        >
                            <option value="equal">equal</option>
                            <option value="less">less</option>
                        </select>
                    ) : '(equal / less)'} carbs on my plate compared to protein
                </span>
            );
        }
        if (habitText.includes('Avoid unhealthy (snacks / drinks / snacks and drinks)')) {
            return (
                <span style={{ fontSize: '15px', lineHeight: '1.5', color: '#2c2416' }}>
                    Avoid unhealthy {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_avoid`] || 'snacks'}
                            onChange={(e) => handleChange(`${habitKey}_avoid`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px', borderRadius: '3px' }}
                        >
                            <option value="snacks">snacks</option>
                            <option value="drinks">drinks</option>
                            <option value="snacks and drinks">snacks and drinks</option>
                        </select>
                    ) : '(snacks / drinks / snacks and drinks)'}
                </span>
            );
        }
        if (habitText.includes('Drink _____ litres of water per day')) {
            return (
                <span style={{ fontSize: '15px', lineHeight: '1.5', color: '#2c2416' }}>
                    Drink {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            step="0.5"
                            value={formData[`${habitKey}_water`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_water`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '13px',
                                margin: '0 2px',
                                outline: 'none'
                            }}
                            placeholder="0.0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                        />
                    ) : '_____'} litres of water per day
                </span>
            );
        }
        if (habitText.includes('Get direct sunlight in the morning (hrs / afternoon)')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Get direct sunlight in the morning and/or afternoon
                </span>
            );
        }
        if (habitText.includes('Consistently go to sleep at _____ and wake up at _____')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Consistently go to sleep at {isChecked ? (
                        <input
                            type="time"
                            value={formData[`${habitKey}_bedtime`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_bedtime`, e.target.value)}
                            style={{
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                        />
                    ) : '_____'} and wake up at {isChecked ? (
                        <input
                            type="time"
                            value={formData[`${habitKey}_waketime`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_waketime`, e.target.value)}
                            style={{
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                        />
                    ) : '_____'}
                </span>
            );
        }
        if (habitText.includes('Wind down and prepare for bed _____ hours before sleeping')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Wind down and prepare for bed {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            step="0.1"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_winddown`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_winddown`, e.target.value)}
                            style={{
                                width: '60px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="0.0"
                        />
                    ) : '_____'} hours before sleeping
                </span>
            );
        }
        if (habitText.includes('Avoid screens _____ hours before sleeping')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Avoid screens {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            step="0.1"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_screens`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_screens`, e.target.value)}
                            style={{
                                width: '60px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="0.0"
                        />
                    ) : '_____'} hours before sleeping
                </span>
            );
        }
        if (habitText.includes('Avoid anything too stimulating or anxiety-inducing _____ hours before bed')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Avoid anything too stimulating or anxiety-inducing {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            step="0.1"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_stimulating`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_stimulating`, e.target.value)}
                            style={{
                                width: '60px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="0.0"
                        />
                    ) : '__.__'} hours before bed (news / intense exercise / scary or thought-provoking media)
                </span>
            );
        }
        if (habitText.includes('Avoid alcohol _____ hours before bed')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Avoid alcohol {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            step="0.1"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_alcohol`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_alcohol`, e.target.value)}
                            style={{
                                width: '60px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="0.0"
                        />
                    ) : '_____'} hours before bed
                </span>
            );
        }
        if (habitText.includes('Have (one / two / three) evening relaxation habits like')) {
            const activities = ['journaling', 'reading', 'meditating', 'praying', 'stretching', 'breathing'];
            const selectedActivities = formData[`${habitKey}_activities`] || [];

            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Have {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_count`] || 'one'}
                            onChange={(e) => handleChange(`${habitKey}_count`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px' }}
                        >
                            <option value="one">one</option>
                            <option value="two">two</option>
                            <option value="three">three</option>
                        </select>
                    ) : '(one / two / three)'} evening relaxation habits like
                    {isChecked ? (
                        <div style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '8px', marginLeft: '10px', alignItems: 'center', marginTop: '10px' }}>
                            {activities.map(activity => (
                                <label key={activity} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: selectedActivities.includes(activity) ? '#8b7355' : 'transparent', //#f5f5f5
                                    color: selectedActivities.includes(activity) ? 'white' : '#2c2416',
                                    padding: '6px 8px',
                                    borderRadius: '12px',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    border: '1px solid #8b7355',
                                    transition: 'all 0.2s ease'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedActivities.includes(activity)}
                                        onChange={(e) => {
                                            const newSelected = e.target.checked
                                                ? [...selectedActivities, activity]
                                                : selectedActivities.filter(a => a !== activity);
                                            handleChange(`${habitKey}_activities`, newSelected);
                                        }}
                                        style={{ display: 'none' }}
                                    />
                                    {activity}
                                </label>
                            ))}
                        </div>
                    ) : ' (journaling / reading / meditating / praying / stretching / breathing)'}
                </span>
            );
        }

        // Exercise specific handlers
        if (habitText.includes('Stretch for _____ minutes _____ times per week')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Stretch for {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_minutes`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_minutes`, e.target.value)}
                            style={{
                                width: '60px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="000"
                        />
                    ) : '_____'} minutes {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '_____'} times per week
                </span>
            );
        }
        if (habitText.includes('Do any form of physical activity for _____ minutes _____ times a week')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Do any form of physical activity for {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_minutes`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_minutes`, e.target.value)}
                            style={{
                                width: '60px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="000"
                        />
                    ) : '_____'} minutes {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '_____'} times a week
                </span>
            );
        }
        if (habitText.includes('Reach _____ steps per day')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Reach {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_steps`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_steps`, e.target.value)}
                            style={{
                                width: '80px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="0000000"
                        />
                    ) : '_______'} steps per day
                </span>
            );
        }
        if (habitText.includes('Do a (15 / 20 / 30)-minute HIIT work-out')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Do a {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_duration`] || '15'}
                            onChange={(e) => handleChange(`${habitKey}_duration`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px' }}
                        >
                            <option value="15">15</option>
                            <option value="20">20</option>
                            <option value="30">30</option>
                        </select>
                    ) : '(15 / 20 / 30)'}-minute HIIT work-out {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '__'} times a week
                </span>
            );
        }
        if (habitText.includes('Walk for _____ (minutes / km / miles) _____ times a week')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Walk for {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_amount`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_amount`, e.target.value)}
                            style={{
                                width: '60px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="000"
                        />
                    ) : '_____'} {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_unit`] || 'minutes'}
                            onChange={(e) => handleChange(`${habitKey}_unit`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px' }}
                        >
                            <option value="minutes">minutes</option>
                            <option value="km">km</option>
                            <option value="miles">miles</option>
                        </select>
                    ) : '(minutes / km / miles)'} {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '__'} times a week
                </span>
            );
        }
        if (habitText.includes('Run for _____ (minutes / km / miles) _____ times a week')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Run for {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_amount`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_amount`, e.target.value)}
                            style={{
                                width: '60px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="000"
                        />
                    ) : '_____'} {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_unit`] || 'minutes'}
                            onChange={(e) => handleChange(`${habitKey}_unit`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px' }}
                        >
                            <option value="minutes">minutes</option>
                            <option value="km">km</option>
                            <option value="miles">miles</option>
                        </select>
                    ) : '(minutes / km / miles)'} {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '__'} times a week
                </span>
            );
        }
        if (habitText.includes('Participate in my sport of choice which is _____ I will do this _____ times a week')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Participate in my sport of choice which is {isChecked ? (
                        <input
                            type="text"
                            value={formData[`${habitKey}_sport`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_sport`, e.target.value)}
                            style={{
                                width: '100px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="sport name"
                        />
                    ) : '_____'} I will do this {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '__'} times a week
                </span>
            );
        }
        if (habitText.includes('Do an exercise that builds muscles _____ times a week')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Do an exercise that builds muscles {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '__'} times a week
                </span>
            );
        }
        if (habitText.includes('Do some mobility exercises _____ times per week')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Do some mobility exercises {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '__'} times per week
                </span>
            );
        }

        // Stress Management specific handlers
        if (habitText.includes('Spend _____ minutes with myself each morning')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Spend {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_minutes`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_minutes`, e.target.value)}
                            style={{
                                width: '60px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="000"
                        />
                    ) : '_____'} minutes with myself each morning
                </span>
            );
        }
        if (habitText.includes('Maintain a (12 / 13 / 14 / 15 / 16)-hour fast')) {
            return (
                <span style={{ fontSize: '15px', lineHeight: '1.5', color: '#2c2416' }}>
                    Maintain a {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_hours`] || '12'}
                            onChange={(e) => handleChange(`${habitKey}_hours`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px', borderRadius: '3px' }}
                        >
                            <option value="12">12</option>
                            <option value="13">13</option>
                            <option value="14">14</option>
                            <option value="15">15</option>
                            <option value="16">16</option>
                        </select>
                    ) : '(12 / 13 / 14 / 15 / 16)'}-hour fast. These are the hours after dinner where I will not eat or drink until breakfast. This includes all foods and drinks except water
                </span>
            );
        }
        if (habitText.includes('Spend time in nature _____ times a week')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Spend time in nature {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '__'} times a week
                </span>
            );
        }
        if (habitText.includes('Express my thoughts and feelings to (a trusted friend / health professional / journal / recorder / self)')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Express my thoughts and feelings to {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_method`] || 'a trusted friend'}
                            onChange={(e) => handleChange(`${habitKey}_method`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px' }}
                        >
                            <option value="a trusted friend">a trusted friend</option>
                            <option value="health professional">health professional</option>
                            <option value="journal">journal</option>
                            <option value="recorder">recorder</option>
                            <option value="self">self</option>
                        </select>
                    ) : '(a trusted friend / health professional / journal / recorder / self)'} {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '__'} times a week
                </span>
            );
        }
        if (habitText.includes('Do breathwork _____ times (a day / week)')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Do breathwork {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '__'} times {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_frequency`] || 'a day'}
                            onChange={(e) => handleChange(`${habitKey}_frequency`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px' }}
                        >
                            <option value="a day">a day</option>
                            <option value="week">week</option>
                        </select>
                    ) : '(a day / week)'}
                </span>
            );
        }
        if (habitText.includes('Engage in my hobbies _____ times a week')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Engage in my hobbies {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '__'} times a week
                </span>
            );
        }
        if (habitText.includes('Give myself a treat according to my love language _____ times a week')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Give myself a treat according to my love language {isChecked ? (
                        <input
                            type="number"
                            min="0"
                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                            value={formData[`${habitKey}_times`] || ''}
                            onChange={(e) => handleChange(`${habitKey}_times`, e.target.value)}
                            style={{
                                width: '50px',
                                padding: '2px 4px',
                                border: 'none',
                                borderBottom: '1px solid #2c2416',
                                background: 'transparent',
                                fontSize: '15px',
                                margin: '0 5px',
                                outline: 'none'
                            }}
                            placeholder="00"
                        />
                    ) : '__'} times a week
                </span>
            );
        }
        if (habitText.includes('Organise my schedule in advance - e.g (daily / weekly) to-do list')) {
            return (
                <span style={{ fontSize: '15px', color: '#2c2416' }}>
                    Organise my schedule in advance - e.g {isChecked ? (
                        <select
                            className='dropdown-style'
                            value={formData[`${habitKey}_type`] || 'daily'}
                            onChange={(e) => handleChange(`${habitKey}_type`, e.target.value)}
                            style={{ margin: '0 5px', padding: '2px', fontSize: '15px' }}
                        >
                            <option value="daily">daily</option>
                            <option value="weekly">weekly</option>
                        </select>
                    ) : '(daily / weekly)'} to-do list
                </span>
            );
        }
        // Handle other cases with simple underscores or dropdowns
        const parts = habitText.split('_____');
        if (parts.length === 1) {
            return <span style={{ fontSize: '15px', color: '#2c2416' }}>{habitText}</span>;
        }

        return (
            <span style={{ fontSize: '15px', color: '#2c2416' }}>
                {parts.map((part, index) => (
                    <React.Fragment key={index}>
                        {part}
                        {index < parts.length - 1 && (
                            <input
                                type="text"
                                style={{
                                    width: '60px',
                                    padding: '2px 4px',
                                    border: 'none',
                                    borderBottom: '1px solid #2c2416',
                                    background: 'transparent',
                                    fontSize: '15px',
                                    marginLeft: '2px',
                                    marginRight: '2px',
                                    outline: 'none',
                                    color: '#2c2416'
                                }}
                                placeholder=""
                                value={formData[`${habitKey}_${index}`] || ''}
                                onChange={(e) => handleChange(`${habitKey}_${index}`, e.target.value)}
                            />
                        )}
                    </React.Fragment>
                ))}
            </span>
        );
    };

    const isLocked = entryMetadata.lockedAt !== null;
    const modalConfig = getJournalStartConfig('transformation');


    return (
        <>
            <JournalStartModal
                isOpen={showStartModal}
                onBegin={handleBegin}
                onCancel={handleNotNow}
                title={modalConfig.title}
                message={modalConfig.message}
                estimatedTime={modalConfig.estimatedTime}
            />
            <div className="self-assessment-container">
                <LockIndicator
                    lockedAt={entryMetadata.lockedAt}
                    startedAt={entryMetadata.startedAt}
                />


                <div className="journal-page-wrapper">
                    <h1 className="journal-title">Transformation</h1>

                    {/* T1: Transformation Instructions */}
                    <div style={{ marginTop: '60px' }}>
                        <p className="journal-prompt" style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '30px' }}>
                            Now that you understand your lifestyle priorities, it's time to make some changes! The next few pages contain health
                            habits that you can adopt - as slowly or as quickly as you want. Below are some guidelines that you can take to make
                            your healthier future a reality.
                        </p>

                        <ol style={{ fontSize: '16px', fontWeight: '600', marginBottom: '30px', color: '#2c2416', paddingLeft: '20px', textAlign: 'justify' }}>
                            <li style={{ marginBottom: '15px', lineHeight: '1.6' }}>
                                Scroll to the page that has your <em>non-negotiable</em> - the pillar of health that you ranked as a no. 1 priority.
                            </li>
                            <li style={{ marginBottom: '15px', lineHeight: '1.6' }}>
                                First, choose the habits in the following pages that match your <em>non-negotiable</em>. For example, if your first
                                health priority is 'exercise', then scroll to the 'exercise' page first to select your habits.
                            </li>
                            <li style={{ lineHeight: '1.6' }}>
                                You may also select habits for the pillars of health that you marked as no. 2, 3 and 4.
                            </li>
                        </ol>

                        <p className="journal-prompt" style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '25px' }}>
                            Once you have your habits, find practical and creative ways to make them a part of your daily life. You may focus on
                            one health habit per week and then stack it with another habit in subsequent weeks. Perhaps begin with as many
                            health habits as you can and progressively increase these habits in the next three months.
                        </p>

                        <p className="journal-prompt" style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '25px', fontWeight: '600' }}>
                            Few things to remember:
                        </p>

                        <ul style={{ fontSize: '15px', lineHeight: '1.8', marginLeft: '30px', marginBottom: '40px', color: '#3d3425', textAlign: 'justify' }}>
                            <li style={{ marginBottom: '15px' }}>
                                You do not have to incorporate <em>all</em> health habits. It's okay to take it step-by-step - you can make this easy for
                                yourself or you can make it challenging. Both options are okay and depend on your personality and level of
                                discipline. I recommend making it a comfortable challenge. Easy enough to maintain consistently,
                                but challenging enough to develop your self-discipline and see results. More
                                healthy habits = more health benefits
                            </li>

                            <li style={{ marginBottom: '15px' }}>
                                Aim for 100% consistency but understand that 80% consistency is still effective. If you don't comply with a habit
                                today, try again tomorrow - guilt-free!
                            </li>

                            <li style={{ marginBottom: '15px' }}>
                                It's helpful to see your health habits daily. Add your chosen habits to your diary or to-do list, screen-shoot your
                                selected habits and make it your wallpaper - Make it easy to integrate into your schedule.
                            </li>

                            {/* <li style={{ marginBottom: '15px' }}>
                                Easy enough to maintain consistently,
                                but challenging enough to develop your self-discipline and see results. More
                                healthy habits = more health benefits
                            </li> */}

                            <li style={{ marginBottom: '15px' }}>
                                It's okay to regress if necessary. Doing less is better than doing nothing. For example, if you committed to eight
                                new health habits but find it too challenging or too impractical to maintain, rather stick to five new health habits
                                that you can do comfortably (instead of giving up on them entirely). You can try to stack more when you're ready.
                            </li>
                        </ul>

                        <p className="handwritten-signature" style={{
                            // fontSize: '36px',
                            fontFamily: "'Brush Script MT', cursive",
                            textAlign: 'center',
                            marginTop: '60px',
                            display: 'block',
                            color: '#2c2416'
                        }}>
                            Never give up on yourself!
                        </p>
                    </div>

                    {/* T2: Nutrition */}
                    <div style={{ marginTop: '80px' }}>
                        <h2 className="journal-title" style={{ fontSize: '40px', marginBottom: '30px' }}>Nutrition</h2>

                        <p
                            // className="journal-prompt"
                            className="handwritten-label"
                            style={{
                                // fontSize: '18px', 
                                fontStyle: 'italic', marginBottom: '30px'
                            }}>
                            I've ranked this as my <input
                                type="number"
                                min="1"
                                max="4"
                                value={formData.nutritionPriority}
                                onChange={(e) => handleChange('nutritionPriority', e.target.value)}
                                style={{
                                    border: 'none',
                                    borderBottom: '2px solid #8b7355',
                                    background: 'transparent',
                                    width: '60px',
                                    textAlign: 'center',
                                    fontFamily: 'inherit',
                                    fontSize: 'inherit',
                                    fontStyle: 'italic',
                                    padding: '2px 5px',
                                    outline: 'none'
                                }}
                            /><span className='ranking-prefix'>
                                {formData.nutritionPriority ? getOrdinalSuffix(formData.nutritionPriority).slice(1) : ''}
                            </span> priority. In the next two weeks, I will...
                        </p>

                        <p className="journal-prompt" style={{ fontSize: '15px', marginBottom: '30px' }}>
                            {/* Tick your selected habits below and click your options where applicable. */}
                            Tick your selection below and click your options where applicable.
                        </p>

                        <div style={{ marginTop: '30px' }}>
                            {[
                                { value: 'meals', label: 'Eat (two / three) meals a day', hasInput: true },
                                { value: 'fasting', label: 'Maintain a (12 / 13 / 14 / 15 / 16)-hour fast. These are the hours after dinner where I will not eat or drink until breakfast. This includes all foods and drinks except water', hasInput: true },
                                { value: 'noSnacking', label: 'Not snack between meals. Any snacks will be eaten directly after meals' },
                                { value: 'avoidUnhealthy', label: 'Avoid unhealthy (snacks / drinks / snacks and drinks)' },
                                { value: 'avoidAlcohol', label: 'Avoid alcohol' },
                                { value: 'fibreVeg', label: 'Eat at least (one / two / three) sources of (fibre / vegetable) in each meal', hasInput: true },
                                { value: 'protein', label: 'Eat at least one source of protein in each meal' },
                                { value: 'proteinGrams', label: 'Eat a minimum of _____ grams of protein per day', hasInput: true },
                                { value: 'healthyFat', label: 'Eat at least one source of healthy fat in each meal' },
                                { value: 'equalCarbs', label: 'Have (equal / less) carbs on my plate compared to protein' },
                                { value: 'avoidProcessed', label: 'Avoid ultra-processed foods' },
                                { value: 'avoidIntolerances', label: 'Avoid my food intolerances' },
                                { value: 'water', label: 'Drink _____ litres of water per day', hasInput: true }
                            ].map((habit, index) => {
                                const isChecked = (formData.nutritionHabits || []).includes(habit.value);
                                return (
                                    <div key={habit.value} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '15px' }}>
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => handleCheckboxChange('nutritionHabits', habit.value)}
                                            style={{ marginRight: '12px', marginTop: '2px', width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
                                            disabled={isLocked}
                                        />
                                        {renderHabitWithInputs(habit.label, `nutrition_habit_${index}`, isChecked)}
                                    </div>
                                );
                            })}

                            <div style={{ marginTop: '30px' }}>
                                <label style={{ fontSize: '15px', display: 'block', marginBottom: '10px', color: '#2c2416' }}>Other:</label>
                                <div className="journal-line">
                                    <input
                                        type="text"
                                        value={formData.nutritionOther}
                                        onChange={(e) => handleChange('nutritionOther', e.target.value)}
                                        placeholder=""
                                        disabled={isLocked}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* T3: Sleep */}
                    <div style={{ marginTop: '80px' }}>
                        <h2 className="journal-title" style={{ fontSize: '40px', marginBottom: '30px' }}>Sleep</h2>

                        <p
                            className="handwritten-label"
                            // className="journal-prompt" 
                            style={{
                                // fontSize: '18px', 
                                fontStyle: 'italic', marginBottom: '30px'
                            }}>
                            I've ranked this as my <input
                                type="number"
                                min="1"
                                max="4"
                                value={formData.sleepPriority}
                                onChange={(e) => handleChange('sleepPriority', e.target.value)}
                                style={{
                                    border: 'none',
                                    borderBottom: '2px solid #8b7355',
                                    background: 'transparent',
                                    width: '60px',
                                    textAlign: 'center',
                                    fontFamily: 'inherit',
                                    fontSize: 'inherit',
                                    fontStyle: 'italic',
                                    padding: '2px 5px'
                                }}
                            /><span
                                className='ranking-prefix'
                            // style={{ fontSize: '18px', fontStyle: 'italic', marginLeft: '5px' }}
                            >
                                {formData.sleepPriority ? getOrdinalSuffix(formData.sleepPriority).slice(1) : ''}
                            </span> priority. In the next two weeks, I will...
                        </p>

                        <p className="journal-prompt" style={{ fontSize: '15px', marginBottom: '30px' }}>
                            {/* Tick your selected habits below and click your options where applicable. */}
                            Tick your selection below and click your options where applicable.
                        </p>

                        <div style={{ marginTop: '30px' }}>
                            {[
                                { value: 'sunlight', label: 'Get direct sunlight in the morning (hrs / afternoon)', hasInput: true },
                                { value: 'movement', label: 'Practice my favourite form of movement during the day' },
                                { value: 'noCaffeine', label: 'Have no caffeine within 10 hours before bed' },
                                { value: 'consistent', label: 'Consistently go to sleep at _____ and wake up at _____', hasInput: true },
                                { value: 'windDown', label: 'Wind down and prepare for bed _____ hours before sleeping', hasInput: true },
                                { value: 'homeDim', label: 'Keep my home as dim as possible in the evening and as dark as possible while sleeping' },
                                { value: 'avoidScreens', label: 'Avoid screens _____ hours before sleeping', hasInput: true },
                                { value: 'avoidStimulating', label: 'Avoid anything too stimulating or anxiety-inducing _____ hours before bed (news / info / exercise / scary or thought-provoking media)', hasInput: true },
                                { value: 'lightDinner', label: 'Have an earlier and/or lighter dinner' },
                                { value: 'avoidAlcoholBeforeBed', label: 'Avoid alcohol _____ hours before bed', hasInput: true },
                                { value: 'eveningHabits', label: 'Have (one / two / three) evening relaxation habits like (journaling / reading / meditating / praying / stretching / breathing)', hasInput: true }
                            ].map((habit, index) => {
                                const isChecked = (formData.sleepHabits || []).includes(habit.value);
                                return (
                                    <div key={habit.value} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '15px' }}>
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => handleCheckboxChange('sleepHabits', habit.value)}
                                            style={{ marginRight: '12px', marginTop: '2px', width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
                                            disabled={isLocked}
                                        />
                                        {renderHabitWithInputs(habit.label, `sleep_habit_${index}`, isChecked)}
                                    </div>
                                );
                            })}

                            <div style={{ marginTop: '30px' }}>
                                <label style={{ fontSize: '15px', display: 'block', marginBottom: '10px', color: '#2c2416' }}>Other:</label>
                                <div className="journal-line">
                                    <input
                                        type="text"
                                        value={formData.sleepOther}
                                        onChange={(e) => handleChange('sleepOther', e.target.value)}
                                        placeholder=""
                                        disabled={isLocked}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* T4: Exercise */}
                    <div style={{ marginTop: '80px' }}>
                        <h2 className="journal-title" style={{ fontSize: '40px', marginBottom: '30px' }}>Exercise</h2>

                        <p
                            className="handwritten-label"
                            // className="journal-prompt" 
                            style={{
                                // fontSize: '18px', 
                                fontStyle: 'italic', marginBottom: '30px'
                            }}>
                            I've ranked this as my <input
                                type="number"
                                min="1"
                                max="4"
                                value={formData.exercisePriority}
                                onChange={(e) => handleChange('exercisePriority', e.target.value)}
                                style={{
                                    border: 'none',
                                    borderBottom: '2px solid #8b7355',
                                    background: 'transparent',
                                    width: '60px',
                                    textAlign: 'center',
                                    fontFamily: 'inherit',
                                    fontSize: 'inherit',
                                    fontStyle: 'italic',
                                    padding: '2px 5px'
                                }}
                            /><span
                                className='ranking-prefix'
                            // style={{ fontSize: '18px', fontStyle: 'italic', marginLeft: '5px' }}
                            >
                                {formData.exercisePriority ? getOrdinalSuffix(formData.exercisePriority).slice(1) : ''}
                            </span> priority. In the next two weeks, I will...
                        </p>

                        <p className="journal-prompt" style={{ fontSize: '15px', marginBottom: '30px' }}>
                            {/* Tick your selected habits below and click your options where applicable. */}
                            Tick your selection below and click your options where applicable.
                        </p>

                        <div style={{ marginTop: '30px' }}>
                            {[
                                { value: 'stretch', label: 'Stretch for _____ minutes _____ times per week', hasInput: true },
                                { value: 'physical', label: 'Do any form of physical activity for _____ minutes _____ times a week', hasInput: true },
                                { value: 'steps', label: 'Reach _____ steps per day', hasInput: true },
                                { value: 'walk', label: 'Walk for _____ (minutes / km / miles) _____ times a week', hasInput: true },
                                { value: 'run', label: 'Run for _____ (minutes / km / miles) _____ times a week', hasInput: true },
                                { value: 'hiit', label: 'Do a (15 / 20 / 30)-minute HIIT work-out _____ times a week', hasInput: true },
                                { value: 'sport', label: 'Participate in my sport of choice which is _____ I will do this _____ times a week', hasInput: true },
                                { value: 'buildMuscles', label: 'Do an exercise that builds muscles _____ times a week', hasInput: true },
                                { value: 'mobility', label: 'Do some mobility exercises _____ times per week', hasInput: true },
                                { value: 'exerciseRestDay', label: 'Have at least one day of rest and recovery' }
                            ].map((habit, index) => {
                                const isChecked = (formData.exerciseHabits || []).includes(habit.value);
                                return (
                                    <div key={habit.value} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '15px' }}>
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => handleCheckboxChange('exerciseHabits', habit.value)}
                                            style={{ marginRight: '12px', marginTop: '2px', width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
                                            disabled={isLocked}
                                        />
                                        {renderHabitWithInputs(habit.label, `exercise_habit_${index}`, isChecked)}
                                    </div>
                                );
                            })}

                            <div style={{ marginTop: '30px' }}>
                                <label style={{ fontSize: '15px', display: 'block', marginBottom: '10px', color: '#2c2416' }}>Other:</label>
                                <div className="journal-line">
                                    <input
                                        type="text"
                                        value={formData.exerciseOther}
                                        onChange={(e) => handleChange('exerciseOther', e.target.value)}
                                        placeholder=""
                                        disabled={isLocked}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* T5: Stress Management */}
                    <div style={{ marginTop: '80px' }}>
                        <h2 className="journal-title" style={{ fontSize: '40px', marginBottom: '30px' }}>Stress management</h2>

                        <p
                            className="handwritten-label"
                            // className="journal-prompt" 
                            style={{
                                // fontSize: '18px', 
                                fontStyle: 'italic', marginBottom: '30px'
                            }}>
                            I've ranked this as my <input
                                type="number"
                                min="1"
                                max="4"
                                value={formData.stressPriority}
                                onChange={(e) => handleChange('stressPriority', e.target.value)}
                                style={{
                                    border: 'none',
                                    borderBottom: '2px solid #8b7355',
                                    background: 'transparent',
                                    width: '60px',
                                    textAlign: 'center',
                                    fontFamily: 'inherit',
                                    fontSize: 'inherit',
                                    fontStyle: 'italic',
                                    padding: '2px 5px'
                                }}
                            /><span
                                className='ranking-prefix'
                            // style={{ fontSize: '18px', fontStyle: 'italic', marginLeft: '5px' }}
                            >
                                {formData.stressPriority ? getOrdinalSuffix(formData.stressPriority).slice(1) : ''}
                            </span> priority. In the next two weeks, I will...
                        </p>

                        <p className="journal-prompt" style={{ fontSize: '15px', marginBottom: '30px' }}>
                            {/* Tick your selected habits below and click your options where applicable. */}
                            Tick your selection below and click your options where applicable.
                        </p>

                        <div style={{ marginTop: '30px' }}>
                            {[
                                { value: 'relax', label: 'Take at least one hour per day to relax' },
                                { value: 'restDay', label: 'Have a full day of rest each week' },
                                { value: 'morningTime', label: 'Spend _____ minutes with myself each morning', hasInput: true },
                                { value: 'schedule', label: 'Organise my schedule in advance - e.g (daily / weekly) to-do list' },
                                { value: 'breathingRoom', label: 'Leave some breathing room in my schedule and avoid overcommitting myself' },
                                { value: 'boundaries', label: 'Enforce boundaries to protect my mental and emotional health' },
                                { value: 'avoidMedia', label: 'Avoid media that is increasing my stress load' },
                                { value: 'nature', label: 'Spend time in nature _____ times a week', hasInput: true },
                                { value: 'express', label: 'Express my thoughts and feelings to (a trusted friend / health professional / journal / recorder / self) _____ times a week', hasInput: true },
                                { value: 'breathwork', label: 'Do breathwork _____ times (a day / week)', hasInput: true },
                                { value: 'grateful', label: 'List 10 things that I am grateful for daily' },
                                { value: 'hobbies', label: 'Engage in my hobbies _____ times a week', hasInput: true },
                                { value: 'loveLanguage', label: 'Give myself a treat according to my love language _____ times a week', hasInput: true }
                            ].map((habit, index) => {
                                const isChecked = (formData.stressHabits || []).includes(habit.value);
                                return (
                                    <div key={habit.value} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '15px' }}>
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => handleCheckboxChange('stressHabits', habit.value)}
                                            style={{ marginRight: '12px', marginTop: '2px', width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
                                            disabled={isLocked}
                                        />
                                        {renderHabitWithInputs(habit.label, `stress_habit_${index}`, isChecked)}
                                    </div>
                                );
                            })}

                            <div style={{ marginTop: '30px' }}>
                                <label style={{ fontSize: '15px', display: 'block', marginBottom: '10px', color: '#2c2416' }}>Other:</label>
                                <div className="journal-line">
                                    <input
                                        type="text"
                                        value={formData.stressOther}
                                        onChange={(e) => handleChange('stressOther', e.target.value)}
                                        placeholder=""
                                        disabled={isLocked}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* T6: Commit */}
                    <div style={{ marginTop: '80px' }}>
                        <h2 className="journal-title" style={{ fontSize: '40px', marginBottom: '40px' }}>Commit</h2>

                        <div className="journal-line" style={{ marginBottom: '40px', maxWidth: '200px' }}>
                            <label style={{ fontSize: '16px', display: 'block', marginBottom: '8px', color: '#2c2416' }}>Date</label>
                            <input
                                type="date"
                                value={formData.commitDate}
                                onChange={(e) => handleChange('commitDate', e.target.value)}
                                placeholder=""
                                disabled={isLocked}
                            />
                        </div>

                        <p className="journal-prompt" style={{ fontSize: '15px', lineHeight: '1.7', marginBottom: '30px' }}>
                            This is a page where you can list all the health habits that you selected for a quick reference. List your non-negotiables first!
                        </p>

                        <p className="handwritten-label" style={{ fontStyle: 'italic', marginBottom: '30px', color: '#2c2416' }}>
                            In the next two weeks, I will...
                        </p>

                        <div className="journal-line multi-line" style={{ marginTop: '30px' }}>
                            <textarea
                                value={formData.commitGoals}
                                onChange={(e) => handleChange('commitGoals', e.target.value)}
                                placeholder=""
                                rows={16}
                                disabled={isLocked}
                            />
                        </div>

                        <p className="journal-prompt" style={{ fontSize: '14px', lineHeight: '1.8', marginTop: '50px', color: '#3d3425' }}>
                            For the next 2 weeks try to be consistent with the plans that you made with yourself. This builds self-fidelity a.k.a
                            self-confidence. An imperfect day is not a bad day, you have another opportunity - tomorrow. All the best,
                            remember to do a check-in after two weeks!
                        </p>
                    </div>

                    {/* T7: My Progress */}
                    <div style={{ marginTop: '80px' }}>
                        <h2 className="journal-title" style={{ fontSize: '32px', marginBottom: '20px' }}>My progress</h2>
                        <p className="journal-prompt" style={{ fontSize: '16px', marginBottom: '40px' }}>How are things going?</p>

                        <div style={{ position: 'relative', overflowX: 'auto', margin: '0 -20px', padding: '0 20px' }}>
                            <table className="progress-calendar-table" style={{ minWidth: '320px' }}>
                                <tbody>
                                    {/* Week 1 Header */}
                                    <tr className="progress-calendar-row">
                                        {[1, 2, 3, 4, 5, 6, 7].map(day => (
                                            <td key={day} className="progress-calendar-header">
                                                Day {day}
                                            </td>
                                        ))}
                                    </tr>
                                    {/* Week 1 Moods */}
                                    <tr className="progress-calendar-row">
                                        {[1, 2, 3, 4, 5, 6, 7].map(day => (
                                            <td key={day} className="progress-calendar-cell">
                                                <div style={{ display: 'flex', justifyContent: 'center', gap: '5px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                    {moodEmojis.map((emoji, index) => (
                                                        <button
                                                            key={index}
                                                            onClick={(e) => handleMoodClick(day, emoji, e)}
                                                            style={{
                                                                cursor: 'pointer',
                                                                fontSize: '24px',
                                                                opacity: formData[`day${day}Mood`] === emoji ? 1 : 0.3,
                                                                transition: 'opacity 0.2s',
                                                                userSelect: 'none',
                                                                background: 'none',
                                                                border: 'none',
                                                                padding: 0
                                                            }}
                                                        >
                                                            {emoji}
                                                        </button>
                                                    ))}
                                                </div>
                                            </td>
                                        ))}
                                    </tr>
                                    {/* Week 2 Header */}
                                    <tr className="progress-calendar-row">
                                        {[8, 9, 10, 11, 12, 13, 14].map(day => (
                                            <td key={day} className="progress-calendar-header">
                                                Day {day}
                                            </td>
                                        ))}
                                    </tr>
                                    {/* Week 2 Moods */}
                                    <tr className="progress-calendar-row">
                                        {[8, 9, 10, 11, 12, 13, 14].map(day => (
                                            <td key={day} className="progress-calendar-cell">
                                                <div style={{ display: 'flex', justifyContent: 'center', gap: '5px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                    {moodEmojis.map((emoji, index) => (
                                                        <button
                                                            key={index}
                                                            onClick={(e) => handleMoodClick(day, emoji, e)}
                                                            style={{
                                                                cursor: 'pointer',
                                                                fontSize: '24px',
                                                                opacity: formData[`day${day}Mood`] === emoji ? 1 : 0.3,
                                                                transition: 'opacity 0.2s',
                                                                userSelect: 'none',
                                                                background: 'none',
                                                                border: 'none',
                                                                padding: 0
                                                            }}
                                                        >
                                                            {emoji}
                                                        </button>
                                                    ))}
                                                </div>
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>

                            {/* Emoji Popup */}
                            {emojiPopup.show && (
                                <div
                                    style={{
                                        position: 'fixed',
                                        top: emojiPopup.position ? emojiPopup.position.y - 60 : '50%',
                                        left: emojiPopup.position ? emojiPopup.position.x - 40 : '50%',
                                        transform: 'translate(-50%, -50%)',
                                        backgroundColor: emojiPopup.emoji === '😊' ? '#FEF3C7' :
                                            emojiPopup.emoji === '😐' ? '#FED7AA' : '#FECACA',
                                        borderRadius: '50%',
                                        width: '80px',
                                        height: '80px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '40px',
                                        zIndex: 1000,
                                        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                                        animation: 'popupBounce 0.6s ease-out'
                                    }}
                                >
                                    {emojiPopup.emoji}
                                </div>
                            )}
                        </div>

                        {/* Notes Section - Now Editable */}
                        <div style={{ marginTop: '60px' }}>
                            <h2 className="journal-title" style={{ fontSize: '32px', marginBottom: '30px' }}>Notes</h2>
                            <div className="journal-line multi-line">
                                <textarea
                                    value={formData.progressNotes}
                                    onChange={(e) => handleChange('progressNotes', e.target.value)}
                                    placeholder=""
                                    rows={12}
                                    style={{ width: '100%', lineHeight: '32px' }}
                                    disabled={isFieldDisabled('progressNotes', isLocked)}
                                />
                            </div>
                        </div>

                        <JournalNavigation
                            prevPage="/journal/reflection"
                            nextPage="/journal/checkin-01"
                        />
                    </div>
                </div>

            </div>
        </>
    );
};

export default Transformation;
